/**
 * Typed client for the unified `/api/identity/*` Identity API.
 *
 * Phase 2 of the auth-flow convergence: this module replaces the
 * legacy `client.post(new Authenticate({...}))` ServiceStack DTO path
 * in login.tsx with direct `fetch()` calls against the modern Identity
 * surface. The endpoints behind these calls are mode-transparent:
 *
 *   • Self-hosted — Edge serves `/api/identity/*` directly via
 *     `Edge.ServiceInterface/IdentityServices.cs` (SignInManager-backed,
 *     ASP.NET Identity ticket cookie renamed `inspire-session`).
 *   • Cloud — `inspire-auth` Worker forwards to the same Edge surface
 *     after CF-edge anti-fraud (rate limit + Turnstile) and region-
 *     pinning.
 *
 * Browser session cookies are NOT JWTs. They're encrypted Identity
 * tickets managed by ASP.NET Core's cookie auth scheme — set by
 * `SignInManager.SignInAsync` on the success paths, validated on
 * every subsequent request by the cookie middleware. This matches
 * the documented Vercel / Linear / Stripe / GitHub / GitLab pattern
 * for primary web-session storage.
 *
 * JWTs in this codebase appear only for short-lived signed claims
 * (the `locateToken` and `mfaToken` shuttled between request steps
 * here) and for service-to-service auth (Worker→Edge HMAC channel).
 *
 * All requests use `credentials: 'same-origin'` so the browser
 * automatically attaches the `inspire-session` cookie to subsequent
 * authenticated calls and accepts `Set-Cookie` headers on the success
 * paths. The Edge.Client SPA is always served from the same origin as
 * the API (`inspire.optymyse.com` in cloud, the operator's hostname
 * in self-hosted), so the cookie's host-only scope is the safest
 * possible posture.
 */

// ─── Discriminated failure surface ─────────────────────────────

export type ApiErrorKind =
    | 'network'      // offline OR transport failure
    | 'rate-limit'   // 429 with optional Retry-After
    | 'server'       // 5xx
    | 'bad-request'  // 4xx other than 401/403/429
    | 'unauthorized' // 401
    | 'forbidden'    // 403

export class ApiError extends Error {
    readonly kind: ApiErrorKind
    readonly code: string
    readonly requestId: string | null
    readonly retryAfterSeconds: number | null
    readonly status: number

    constructor(init: {
        kind: ApiErrorKind
        code: string
        message: string
        requestId: string | null
        retryAfterSeconds?: number | null
        status?: number
    }) {
        super(init.message)
        this.kind = init.kind
        this.code = init.code
        this.requestId = init.requestId
        this.retryAfterSeconds = init.retryAfterSeconds ?? null
        this.status = init.status ?? 0
    }
}

/** Grep-able reference shown under user-facing error messages (Stripe / Vercel style). */
export function formatSupportRef(err: ApiError): string {
    return err.requestId ? `${err.code} · ${err.requestId}` : err.code
}

// ─── Response shapes ──────────────────────────────────────────

export interface LocateBranding {
    tenantId: string
    tenantName: string
    /** Same-origin relative path. */
    logoUrl: string
}

export interface OrgChoice {
    region: string
    /** Tenant display name when the region has a single-tenant branding match.
     *  Absent when the locate response doesn't carry per-region tenant info,
     *  in which case the picker falls back to a region-code → friendly-name
     *  map in the SignIn component. */
    label?: string
}

export type LocateResponse = LocateResponseOk | LocateChallenge

export interface LocateResponseOk {
    locateToken: string
    orgPicker: boolean
    orgChoices?: OrgChoice[]
    branding?: LocateBranding | null
    /** Always absent on the success path; presence indicates the challenge variant. */
    challenge?: undefined
}

export interface LocateChallenge {
    locateToken?: undefined
    challenge: 'turnstile'
    siteKey: string
}

export function isLocateChallenge(r: LocateResponse): r is LocateChallenge {
    return (r as LocateChallenge).challenge === 'turnstile'
}

export type AuthenticateResponse =
    | AuthenticateSuccess
    | AuthenticateMfaPending
    | AuthenticateFailure

export interface AuthenticateSuccess {
    ok: true
    mfaRequired: false
    mustChangePassword: boolean
    displayName: string | null
}

export interface AuthenticateMfaPending {
    ok: true
    mfaRequired: true
    mfaToken: string
}

export interface AuthenticateFailure {
    ok: false
}

export type VerifyMfaResponse = VerifyMfaSuccess | AuthenticateFailure

export interface VerifyMfaSuccess {
    ok: true
    mustChangePassword: boolean
    displayName: string | null
}

export interface IdentityMeResponse {
    userId: string
    email: string
    displayName: string | null
    firstName: string | null
    lastName: string | null
    profileUrl: string | null
    tenantId: string
    tenantIds: string[]
    roles: string[]
    mfaEnabled: boolean
    mustChangePassword: boolean
    region: string
}

export interface ForgotPasswordResponse {
    ok: boolean
}

export interface ResetPasswordResponse {
    ok: boolean
    failureCode?: 'invalid' | 'weak_password' | null
    errors?: string[]
}

export type AcceptInviteResponse = ResetPasswordResponse

export interface RefreshResponse {
    ok: boolean
}

export interface LogoutResponse {
    ok: boolean
}

// ─── HTTP helper — shared by every endpoint below ─────────────

async function postJson<T>(path: string, body: unknown, opName: string): Promise<T> {
    let response: Response
    try {
        response = await fetch(path, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(body),
        })
    } catch (err) {
        throw classifyThrown(err, opName)
    }

    if (response.ok) {
        return (await response.json()) as T
    }

    // 400/401 from identity endpoints carry the canonical structured
    // body (anti-enumerated `{ ok: false }` or success-with-mfa-pending
    // shapes). Pattern-match in callers rather than throwing.
    if (response.status === 400 || response.status === 401) {
        return (await response.json()) as T
    }

    throw await classifyResponse(response, opName)
}

async function getJson<T>(path: string, opName: string): Promise<T> {
    let response: Response
    try {
        response = await fetch(path, {
            method: 'GET',
            credentials: 'same-origin',
            headers: { accept: 'application/json' },
        })
    } catch (err) {
        throw classifyThrown(err, opName)
    }

    if (response.ok) {
        return (await response.json()) as T
    }

    if (response.status === 400 || response.status === 401) {
        return (await response.json()) as T
    }

    throw await classifyResponse(response, opName)
}

function classifyThrown(err: unknown, opName: string): ApiError {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return new ApiError({
            kind: 'network',
            code: 'INS-NET-OFF',
            message: "You're offline. Reconnect and try again.",
            requestId: null,
        })
    }
    const message = err instanceof Error ? err.message : `Couldn't ${opName}.`
    return new ApiError({
        kind: 'network',
        code: 'INS-NET',
        message,
        requestId: null,
    })
}

async function classifyResponse(response: Response, opName: string): Promise<ApiError> {
    const requestId =
        response.headers.get('cf-ray') ?? response.headers.get('x-correlation-id') ?? null
    const retryAfter = response.headers.get('retry-after')

    if (response.status === 429) {
        return new ApiError({
            kind: 'rate-limit',
            code: 'INS-429',
            message: 'Too many attempts. Please wait a moment before trying again.',
            requestId,
            retryAfterSeconds: parseRetryAfter(retryAfter) ?? 60,
            status: 429,
        })
    }
    if (response.status === 403) {
        return new ApiError({
            kind: 'forbidden',
            code: 'INS-403',
            message: "You don't have access to that.",
            requestId,
            status: 403,
        })
    }
    if (response.status >= 500) {
        return new ApiError({
            kind: 'server',
            code: `INS-${response.status}`,
            message: 'The service is having trouble right now. Please try again shortly.',
            requestId,
            status: response.status,
        })
    }
    return new ApiError({
        kind: 'bad-request',
        code: `INS-${response.status}`,
        message: `Couldn't ${opName}.`,
        requestId,
        status: response.status,
    })
}

function parseRetryAfter(value: string | null): number | null {
    if (!value) return null
    const seconds = Number.parseInt(value, 10)
    if (!Number.isNaN(seconds) && seconds > 0) return seconds
    const when = Date.parse(value)
    if (!Number.isNaN(when)) return Math.max(0, Math.ceil((when - Date.now()) / 1000))
    return null
}

// ─── Endpoints ────────────────────────────────────────────────

export async function locate(email: string, turnstileToken?: string): Promise<LocateResponse> {
    const body: { email: string; turnstileToken?: string } = { email }
    if (turnstileToken) body.turnstileToken = turnstileToken
    return postJson<LocateResponse>('/api/identity/locate', body, 'look you up')
}

export async function authenticate(
    locateToken: string,
    password: string,
    region?: string,
): Promise<AuthenticateResponse> {
    return postJson<AuthenticateResponse>(
        '/api/identity/authenticate',
        { locateToken, password, region },
        'sign you in',
    )
}

export async function verifyMfa(mfaToken: string, code: string): Promise<VerifyMfaResponse> {
    return postJson<VerifyMfaResponse>(
        '/api/identity/mfa/verify',
        { mfaToken, code },
        'verify your authentication code',
    )
}

export async function refresh(): Promise<RefreshResponse> {
    return postJson<RefreshResponse>('/api/identity/refresh', {}, 'refresh your session')
}

export async function logout(): Promise<LogoutResponse> {
    return postJson<LogoutResponse>('/api/identity/logout', {}, 'sign you out')
}

export async function me(): Promise<IdentityMeResponse> {
    return getJson<IdentityMeResponse>('/api/identity/me', 'load your account')
}

export async function forgotPassword(email: string): Promise<ForgotPasswordResponse> {
    return postJson<ForgotPasswordResponse>(
        '/api/identity/forgot-password',
        { email },
        'send the reset link',
    )
}

export async function resetPassword(
    email: string,
    token: string,
    newPassword: string,
): Promise<ResetPasswordResponse> {
    return postJson<ResetPasswordResponse>(
        '/api/identity/reset-password',
        { email, token, newPassword },
        'reset your password',
    )
}

export async function acceptInvite(
    email: string,
    token: string,
    newPassword: string,
): Promise<AcceptInviteResponse> {
    return postJson<AcceptInviteResponse>(
        '/api/identity/accept-invite',
        { email, token, newPassword },
        'accept the invitation',
    )
}

// ─── SSO discovery + redirect URL minting ─────────────────────

export interface SsoResolveResponse {
    found: boolean
    tenantId?: string
    tenantSlug?: string | null
    tenantName?: string | null
    providers?: string[]
    logoUrl?: string | null
    logoAlt?: string | null
}

export async function resolveSso(email: string): Promise<SsoResolveResponse> {
    return postJson<SsoResolveResponse>(
        '/api/identity/sso/resolve',
        { email },
        'check single sign-on for your email',
    )
}

export interface SsoAuthorizeUrlResponse {
    authorizeUrl?: string
}

export async function buildOidcAuthorizeUrl(input: {
    tenantSlug: string
    provider: string
    redirectUri: string
    loginHint?: string
}): Promise<SsoAuthorizeUrlResponse> {
    return postJson<SsoAuthorizeUrlResponse>(
        '/api/identity/sso/oidc/authorize-url',
        input,
        'start SSO sign-in',
    )
}

export interface SamlRedirectUrlResponse {
    redirectUrl?: string
}

export async function buildSamlRedirectUrl(input: {
    tenantSlug: string
    redirect?: string
}): Promise<SamlRedirectUrlResponse> {
    return postJson<SamlRedirectUrlResponse>(
        '/api/identity/sso/saml/redirect-url',
        input,
        'start SAML sign-in',
    )
}

// ─── Platform capabilities ────────────────────────────────────

export interface CapabilitiesResponse {
    emailEnabled: boolean
    isCloud: boolean
}

export async function getCapabilities(): Promise<CapabilitiesResponse> {
    return getJson<CapabilitiesResponse>('/api/identity/capabilities', 'load platform capabilities')
}

/** Force-password-change after sign-in. Calls UpdateMyAccount via the
 *  /api/identity/change-password route alias. The session cookie is
 *  required (the user has just signed in and the cookie is set; this
 *  is the "set a real password" step). */
export interface ChangePasswordResponse {
    ok: boolean
    message?: string
}

export async function changePassword(newPassword: string): Promise<ChangePasswordResponse> {
    try {
        const response = await fetch('/api/identity/change-password', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ newPassword }),
        })
        if (response.ok) return { ok: true }
        const body = await response.json().catch(() => ({}))
        return {
            ok: false,
            message:
                (body?.responseStatus?.message as string | undefined) ??
                'Failed to change password',
        }
    } catch {
        return { ok: false, message: 'Network error during password change.' }
    }
}
